import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Send, Megaphone, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";
import { AdminHeader } from "@/components/admin/AdminDashboard";

export default function AdminMessages({ onChange }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // user_id
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [bcastOpen, setBcastOpen] = useState(false);
  const endRef = useRef(null);

  const loadThreads = useCallback(async () => { setThreads(await adminApi.threads()); }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = async (uid) => {
    setActive(uid);
    const t = await adminApi.thread(uid);
    setThread(t);
    onChange?.();
    loadThreads();
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !active) return;
    try {
      await adminApi.sendMessage({ target: "user", user_id: active, body });
      setReply("");
      await openThread(active);
    } catch { toast.error("Could not send message."); }
  };

  return (
    <div>
      <AdminHeader
        eyebrow="COMMS — TWO-WAY MESSAGING"
        title="Messages"
        sub="Message candidates and employers directly, or broadcast to everyone."
        right={<Button data-testid="broadcast-open-button" onClick={() => setBcastOpen(true)} className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11"><Megaphone size={16} className="mr-2" /> Broadcast</Button>}
      />

      <div className="grid lg:grid-cols-3 gap-4 h-[560px]">
        {/* Thread list */}
        <div className={`bg-[#10161F] border border-white/10 overflow-y-auto ${active ? "hidden lg:block" : ""}`} data-testid="admin-threads-list">
          {threads.length === 0 && <div className="p-6 text-sm text-[#8FA3B8]">No conversations yet. Send a broadcast to start.</div>}
          {threads.map((t) => (
            <button key={t.user_id} data-testid={`thread-${t.user_id}`} onClick={() => openThread(t.user_id)} className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/5 transition-colors ${active === t.user_id ? "bg-[#FF5E1E]/10 border-l-2 border-l-[#FF5E1E]" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-display font-bold uppercase text-sm truncate">{t.name}</span>
                {t.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] text-[10px] font-bold shrink-0">{t.unread}</span>}
              </div>
              <div className="font-mono-tech text-[9px] tracking-[0.1em] text-[#8FA3B8] mt-0.5 uppercase">{t.role}</div>
              <div className="text-xs text-[#B8C4D0] truncate mt-1">{t.last_sender === "admin" ? "You: " : ""}{t.last_body}</div>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className={`lg:col-span-2 bg-[#10161F] border border-white/10 flex flex-col ${!active ? "hidden lg:flex" : ""}`}>
          {!thread ? (
            <div className="flex-1 grid place-items-center text-sm text-[#8FA3B8]">Select a conversation to view messages.</div>
          ) : (
            <>
              <div className="p-4 border-b border-white/10 flex items-center gap-3">
                <button onClick={() => { setActive(null); setThread(null); }} className="lg:hidden text-[#8FA3B8]"><ArrowLeft size={18} /></button>
                <div>
                  <div className="font-display font-bold uppercase">{thread.user?.name}</div>
                  <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8]">{thread.user?.email} · {thread.user?.role}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-conversation">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 text-sm ${m.sender === "admin" ? "bg-[#FF5E1E] text-[#0B0F14]" : "bg-[#0B0F14] border border-white/10 text-[#F4F6F8]"}`}>
                      <div>{m.body}</div>
                      <div className={`font-mono-tech text-[8px] tracking-[0.1em] mt-1 ${m.sender === "admin" ? "text-[#0B0F14]/75 font-semibold" : "text-[#8FA3B8]"}`}>{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-white/10 flex gap-2">
                <Textarea data-testid="admin-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }} placeholder="Type a reply…" className="min-h-[44px] max-h-24 rounded-none bg-[#0B0F14] border-white/12 text-white resize-none" />
                <Button data-testid="admin-reply-send" onClick={sendReply} className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] h-auto px-4"><Send size={16} /></Button>
              </div>
            </>
          )}
        </div>
      </div>

      <BroadcastDialog open={bcastOpen} onOpenChange={setBcastOpen} onSent={() => { loadThreads(); onChange?.(); }} />
    </div>
  );
}

function BroadcastDialog({ open, onOpenChange, onSent }) {
  const [target, setTarget] = useState("all");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!body.trim()) { toast.error("Message cannot be empty."); return; }
    setBusy(true);
    try {
      const r = await adminApi.sendMessage({ target, body: body.trim() });
      toast.success(`Broadcast sent to ${r.sent} ${r.sent === 1 ? "person" : "people"}.`);
      setBody(""); onOpenChange(false); onSent?.();
    } catch { toast.error("Could not send broadcast."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#10161F] border-white/15 text-white rounded-none max-w-lg">
        <DialogHeader><DialogTitle className="font-display font-black uppercase flex items-center gap-2"><Megaphone size={18} className="text-[#FF5E1E]" /> Broadcast Message</DialogTitle></DialogHeader>
        <div className="mt-2 space-y-4">
          <div>
            <div className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase mb-1.5">Send to</div>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="broadcast-target" className="rounded-none bg-[#0B0F14] border-white/14 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#10161F] border-white/15 text-white rounded-none">
                <SelectItem value="all" className="focus:bg-[#FF5E1E] focus:text-[#0B0F14]">Everyone</SelectItem>
                <SelectItem value="candidates" className="focus:bg-[#FF5E1E] focus:text-[#0B0F14]">All Candidates</SelectItem>
                <SelectItem value="employers" className="focus:bg-[#FF5E1E] focus:text-[#0B0F14]">All Employers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Your announcement to the workforce…" className="min-h-[120px] rounded-none bg-[#0B0F14] border-white/14 text-white" />
          <Button data-testid="broadcast-send" onClick={send} disabled={busy} className="w-full rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11">{busy ? "Sending…" : "Send Broadcast"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
